import { LogIn } from 'lucide-react';
import { useState, type SyntheticEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAnalyticsAuth } from '@/lib/useAnalyticsAuth';

export const LoginPage = () => {
  const { completeNewPassword, error, login, status } = useAnalyticsAuth();
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [password, setPassword] = useState('');
  const disabled = status === 'loading';
  const needsNewPassword = status === 'newPasswordRequired';
  const missingRequiredFields = needsNewPassword ? !newPassword : !email || !password;
  const submitDisabled = disabled || missingRequiredFields;
  let submitLabel = 'Sign in';
  if (disabled) {
    submitLabel = 'Signing in';
  } else if (needsNewPassword) {
    submitLabel = 'Set password';
  }

  const handleSubmit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (needsNewPassword) {
      void completeNewPassword(newPassword);
      return;
    }
    void login(email, password);
  };

  return (
    <div className="bg-canvas grid min-h-screen place-items-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Internal analytics sign in</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="grid gap-4" onSubmit={handleSubmit}>
            {needsNewPassword ? (
              <label className="text-muted grid gap-1 text-sm font-medium">
                New password
                <Input
                  autoComplete="new-password"
                  disabled={disabled}
                  onChange={(event) => setNewPassword(event.target.value)}
                  type="password"
                  value={newPassword}
                />
              </label>
            ) : (
              <>
                <label className="text-muted grid gap-1 text-sm font-medium">
                  Email
                  <Input
                    autoComplete="email"
                    disabled={disabled}
                    onChange={(event) => setEmail(event.target.value)}
                    type="email"
                    value={email}
                  />
                </label>
                <label className="text-muted grid gap-1 text-sm font-medium">
                  Password
                  <Input
                    autoComplete="current-password"
                    disabled={disabled}
                    onChange={(event) => setPassword(event.target.value)}
                    type="password"
                    value={password}
                  />
                </label>
              </>
            )}
            {error ? (
              <p className="border-critical/30 bg-critical-wash text-critical rounded-md border px-3 py-2 text-sm">{error}</p>
            ) : null}
            <Button className="w-full" disabled={submitDisabled} type="submit" variant="primary">
              <LogIn aria-hidden="true" className="size-4" />
              {submitLabel}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
